import { useRef, useState } from "react";
import { preparePortraitDataUrl } from "./portrait.js";

interface PortraitPickerProps {
  previewUrl: string;
  hasCustomPortrait: boolean;
  disabled?: boolean;
  onSelect: (dataUrl: string) => Promise<void> | void;
  onRemove: () => Promise<void> | void;
}

export function PortraitPicker({ previewUrl, hasCustomPortrait, disabled = false, onSelect, onRemove }: PortraitPickerProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const locked = disabled || processing;

  async function choose(file: File | undefined) {
    if (!file) return;
    setProcessing(true);
    setMessage(null);
    try {
      const dataUrl = await preparePortraitDataUrl(file);
      await onSelect(dataUrl);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The portrait could not be prepared.");
    } finally {
      setProcessing(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function remove() {
    setProcessing(true);
    setMessage(null);
    try {
      await onRemove();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The portrait could not be removed.");
    } finally {
      setProcessing(false);
    }
  }

  return <section className="portrait-picker">
    <div className="portrait-preview"><img src={previewUrl} alt="Character portrait preview" /></div>
    <div className="portrait-copy">
      <p className="eyebrow">CHARACTER PORTRAIT</p>
      <h2>{hasCustomPortrait ? "Your portrait is ready." : "A placeholder will be used."}</h2>
      <p>Upload a JPEG, PNG, or WebP image. It will be resized before it is attached to the official character sheet.</p>
      <div className="portrait-actions">
        <label className={`secondary portrait-upload ${locked ? "disabled" : ""}`}>
          {processing ? "Preparing portrait…" : hasCustomPortrait ? "Replace portrait" : "Upload portrait"}
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" disabled={locked} onChange={(event) => void choose(event.target.files?.[0])} />
        </label>
        {hasCustomPortrait && <button className="danger-link" type="button" disabled={locked} onClick={() => void remove()}>Use placeholder</button>}
      </div>
      <small>Only upload artwork or photographs you have permission to use.</small>
      {message && <div className="notice portrait-notice" role="alert">{message}</div>}
    </div>
  </section>;
}
